// Silent token refresh for the roles-version gate.
//
// When a user's roles change (e.g. via SCIM provisioning) the auth service
// invalidates their already-issued workspace JWT and the JWT middleware rejects
// it with `401` + `X-Token-Stale: 1`. Rather than bounce the user to login, this
// global axios interceptor catches that specific signal, posts the stale token
// to /oidc/auth/refresh to get a fresh one carrying the new role, updates the
// app's stored token, and transparently retries the original request.
//
// A 403 from refresh means the user lost all access (full deprovision) — there
// is nothing to downgrade to, so we clear the session and send them to login.
//
// Note: this covers axios requests (the main app/search/home flows). fetch-based
// calls are not intercepted; after a refresh the persisted token + a subsequent
// navigation/reload bring them in line.

import axios from "axios";
import { route } from "preact-router";
import {
  LOGGED_OUT_PARAM,
  LOGOUT_REASON_HEADER,
  noteSignedOut,
} from "@korra/session";
import { store } from "../redux/store";
import { clearUser, setJWTUserIdToken } from "../redux/slices/globalsSlice";
// Circular with ../utils (which imports clearUserSession from here); both
// sides only call the other at run time, never while the modules load.
import { endCurrentSession } from "../utils";
import { setPreviewSasToken } from "../redux/slices/authSlice";
import { queryClient, userSessions } from "../lib/queryClient";
import { get_AUTH_HOST } from "../configs/env";
import { handleEdgeSessionRejection } from "./edge-session";
import logger from "../logger";

const STALE_HEADER = "x-token-stale";
const SESSION_KEY = "Web-app-edgeUser";

// Guards against registering the interceptor more than once (module re-import
// under HMR), which would retry a failed request once per registration.
let installed = false;

// Single in-flight refresh shared across concurrent 401s.
let refreshing: Promise<string | null> | null = null;

function bearer(token: string): string {
  return token.replace(/^Bearer\s+/i, "").trim();
}

async function performRefresh(staleToken: string): Promise<string | null> {
  try {
    const res = await fetch(`${get_AUTH_HOST()}/oidc/auth/refresh`, {
      method: "POST",
      headers: { Authorization: `Bearer ${staleToken}` },
      mode: "cors",
    });
    if (!res.ok) {
      // 403 => no remaining access (full deprovision); any other failure we also
      // treat as non-recoverable here.
      return null;
    }
    const json = await res.json();
    return json?.data?.userWorkspace?.token || null;
  } catch (e) {
    logger.error({
      message: "Silent token refresh request failed",
      error: e,
      stacktrace: (e as any)?.stack,
    });
    return null;
  }
}

// Update the persisted edge session so a later reload / fetch-based request also
// carries the refreshed token.
function persistEdgeToken(newToken: string): void {
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (!stored) return;
    const parsed = JSON.parse(stored);
    if (parsed?.user) {
      parsed.user.userIdToken = newToken;
      localStorage.setItem(SESSION_KEY, JSON.stringify(parsed));
    }
  } catch {
    /* best effort */
  }
}

// The roles-version gate lives in JWTAuthMiddleware, which runs on cloud AND
// edge, so this can fire in either. Pick the login flow from whether an edge
// session is what we're tearing down, rather than assuming edge.
/**
 * Drop everything minted for the current user, so the next login on this
 * browser never reuses the previous user's CDN token (local_cdn watermarks
 * assets by the token's user), a preview link's token, or identity.
 */
export function clearUserSession(): void {
  userSessions.generation += 1;
  queryClient.removeQueries({ queryKey: ["sasToken"] });
  store.dispatch(clearUser());
  store.dispatch(setPreviewSasToken(""));
}

// The failed refresh, and the stale token, that already signed out. Requests
// that went stale together wait on the same refresh; a late 401 after it
// settled carries the same token. Only the first of either signs out (one
// revoke, one sign-out count, one route).
let signedOutFor: Promise<string | null> | null = null;
let signedOutToken = "";

function logoutToLogin(): void {
  // End the session first: clearUserSession() nulls the sessionId it reads, and
  // this forced logout routes client-side, so no pagehide will end it either.
  endCurrentSession();
  clearUserSession();
  let hadEdgeSession = false;
  try {
    hadEdgeSession = !!localStorage.getItem(SESSION_KEY);
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
  // The marker keeps the edge login page from signing straight back in. The
  // sign-out is published now too, for other tabs, before the page loads.
  if (hadEdgeSession) noteSignedOut();
  route(hadEdgeSession ? `/edge/login?${LOGGED_OUT_PARAM}=1` : "/login", true);
}

export function setupTokenRefreshInterceptor(): void {
  if (installed) return;
  installed = true;

  // NOTE: outgoing API calls are deliberately NOT counted as session activity.
  // Background polls (the 3-min SAS-token refresh, CDN, etc.) would otherwise
  // heartbeat the idle window forever and the 30-min idle logout would never
  // fire (#1100). Real user activity is tracked by the DOM ActivityTracker
  // (keyboard/mouse/touch/scroll/visibility/navigation), and any user-driven API
  // call is already preceded by one of those events, so nothing is lost.
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const resp = error?.response;
      const cfg = error?.config || {};

      // Edge session ended (issue #1100): revoked / past absolute / idle. The
      // server marks it with X-Logout-Reason. Unlike the roles-version stale
      // signal this is terminal — there is no fresh token to fetch — so tear the
      // session down and redirect with the reason instead of retrying.
      const logoutReason =
        resp?.status === 401 &&
        resp.headers &&
        resp.headers[LOGOUT_REASON_HEADER];
      if (logoutReason) {
        // Pass the token this request was sent with, so a late answer for an
        // older session can't end the one in use now (#1100).
        handleEdgeSessionRejection(
          String(logoutReason),
          String(
            cfg.headers?.Authorization || cfg.headers?.authorization || "",
          ),
        );
        return Promise.reject(error);
      }

      const isStale =
        resp?.status === 401 &&
        !!(resp.headers && resp.headers[STALE_HEADER]) &&
        !cfg.__staleRetried;

      if (!isStale) {
        return Promise.reject(error);
      }

      // The token to refresh: prefer the one the failed request used, else the
      // app's current token from redux.
      const authHeader =
        cfg.headers?.Authorization || cfg.headers?.authorization || "";
      const staleToken =
        bearer(String(authHeader)) ||
        (store.getState() as any)?.globals?.jwtUserIdToken ||
        "";

      if (!staleToken) {
        logoutToLogin();
        return Promise.reject(error);
      }

      if (!refreshing) {
        refreshing = performRefresh(staleToken).finally(() => {
          refreshing = null;
        });
      }
      const pending = refreshing;
      const newToken = await pending;

      if (!newToken) {
        if (signedOutFor !== pending && signedOutToken !== staleToken) {
          signedOutFor = pending;
          signedOutToken = staleToken;
          logoutToLogin();
        }
        return Promise.reject(error);
      }

      // Apply the new token everywhere the app reads it, then retry once.
      store.dispatch(setJWTUserIdToken(newToken));
      persistEdgeToken(newToken);
      logger.log({
        message: "Silently refreshed workspace token after role change",
      });

      cfg.__staleRetried = true;
      cfg.headers = {
        ...(cfg.headers || {}),
        Authorization: `Bearer ${newToken}`,
      };
      return axios(cfg);
    },
  );
}
